/**
 * CMP設定管理・バリデーション
 *
 * 初期化時に設定JSONをバリデーションし、不正な値を検出する。
 * 必須項目が欠落している場合はエラーを出力して初期化を中断する。
 */

const REQUIRED_KEYS = ['categories', 'translations', 'defaultLanguage'];

// 色値の許可パターン
// バックエンドの Site::CmpConfigurable::CMP_COLOR_VALUE_REGEXP と同値
// （サーバー側は \A...\z、JS側は ^...$ でアンカー）
export const COLOR_VALUE_REGEXP = /^(#[0-9A-Fa-f]{3,8}|transparent|rgba?\([\d,\s.]+\))$/;

// リージョン別の表示モード（バックエンドの CMP_REGION_DISPLAY_MODES と同値）
const REGION_DISPLAY_MODES = ['normal', 'hidden_no_cmp', 'hidden_default_consent'];

// 色カスタム可能なボタン種別（バックエンドの CMP_REGION_BUTTON_KEYS と同値）。
// show_preferences / revoke はリンク表示固定のため対象外（ui.jsで常にリンク調）。
export const REGION_BUTTON_KEYS = ['accept_all', 'accept_necessary', 'save'];

// 「日本以外」のフォールバック設定の予約キー
const REGION_DEFAULT_KEY = 'default';

/**
 * 設定オブジェクトをバリデーションする
 * @param {Object} raw - パース済みの設定オブジェクト
 * @returns {{ valid: boolean, config: Object|null, errors: string[] }}
 */
export function validateConfig(raw) {
  const errors = [];

  if (!raw || typeof raw !== 'object') {
    return { valid: false, config: null, errors: ['CMP config is not an object'] };
  }

  // 必須キーチェック
  for (const key of REQUIRED_KEYS) {
    if (!(key in raw)) {
      errors.push(`Missing required config key: "${key}"`);
    }
  }

  if (!Array.isArray(raw.categories)) {
    errors.push('"categories" must be an array');
  }

  if (typeof raw.translations !== 'object' || raw.translations === null) {
    errors.push('"translations" must be an object');
  }

  if (typeof raw.defaultLanguage !== 'string') {
    errors.push('"defaultLanguage" must be a string');
  }

  if (errors.length > 0) {
    return { valid: false, config: null, errors };
  }

  // カテゴリバリデーション（不正なカテゴリは除外して続行）
  const validCategories = [];
  for (const cat of raw.categories) {
    if (!cat || typeof cat !== 'object' || typeof cat.key !== 'string' || !cat.key) {
      console.error('[CMP] Invalid category definition, skipping:', cat);
      continue;
    }
    validCategories.push({
      key: cat.key,
      required: Boolean(cat.required),
      gtmSignals: Array.isArray(cat.gtmSignals) ? cat.gtmSignals.filter((s) => typeof s === 'string') : []
    });
  }

  if (validCategories.length === 0) {
    return { valid: false, config: null, errors: ['No valid categories defined'] };
  }

  // Cookie設定のバリデーション・デフォルト化
  const cookieName = (raw.cookie && raw.cookie.name) || 'ck_cmp';
  if (!/^[a-zA-Z0-9_-]+$/.test(cookieName)) {
    errors.push('"cookie.name" contains invalid characters');
  }

  const maxAge = (raw.cookie && raw.cookie.maxAge) || 180;
  if (typeof maxAge !== 'number' || !Number.isFinite(maxAge) || maxAge <= 0) {
    errors.push('"cookie.maxAge" must be a positive number');
  }

  const VALID_SAME_SITE = ['Strict', 'Lax', 'None'];
  const sameSite = (raw.cookie && raw.cookie.sameSite) || 'Lax';
  if (!VALID_SAME_SITE.includes(sameSite)) {
    errors.push(`"cookie.sameSite" must be one of: ${VALID_SAME_SITE.join(', ')}`);
  }

  if (errors.length > 0) {
    return { valid: false, config: null, errors };
  }

  const config = {
    categories: validCategories,
    defaultLanguage: raw.defaultLanguage,
    translations: raw.translations,
    consentLogUrl: raw.consentLogUrl || null,
    privacyUrl: raw.privacyUrl || null,
    cookie: {
      name: cookieName,
      maxAge,
      path: (raw.cookie && raw.cookie.path) || '/',
      sameSite
    }
  };

  if (raw.gtmConsentMode && typeof raw.gtmConsentMode === 'object') {
    config.gtmConsentMode = { enabled: Boolean(raw.gtmConsentMode.enabled) };
  }

  // リージョン設定（regionSettings/regionUrlが存在しない旧形式でも従来どおり動作させる）
  config.regionSettings = validateRegionSettings(raw.regionSettings);
  config.regionUrl = typeof raw.regionUrl === 'string' && raw.regionUrl ? raw.regionUrl : null;

  return { valid: true, config, errors: [] };
}

/**
 * regionSettingsをバリデーションして正規化する
 * サーバー側でもバリデーション済みだが、防御的に二重チェックする。
 * 不正な値はconsole.warnして捨てる（その項目のみデフォルトにフォールバック、全体は壊さない）
 *
 * @param {Object|undefined} raw - { default: { base_button_style }, JP: { display_mode, button_styles, default_consent_categories } }
 * @returns {Object} 正規化済みregionSettings（常にオブジェクト）
 */
function validateRegionSettings(raw) {
  if (raw === undefined || raw === null) return {};
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    console.warn('[CMP] "regionSettings" must be an object, ignoring:', raw);
    return {};
  }

  const normalized = {};

  Object.keys(raw).forEach((key) => {
    const value = raw[key];
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      console.warn(`[CMP] Invalid regionSettings entry "${key}", ignoring:`, value);
      return;
    }

    if (key === REGION_DEFAULT_KEY) {
      // default には base_button_style のみ（display_mode等が混入していても無視）
      const entry = {};
      const baseStyle = validateButtonStyle(value.base_button_style, `${key}.base_button_style`);
      if (baseStyle) entry.base_button_style = baseStyle;
      normalized[key] = entry;
    } else {
      normalized[key] = validateCountrySettings(key, value);
    }
  });

  return normalized;
}

/**
 * 国コードリージョン設定（display_mode / button_styles / default_consent_categories）を検証する
 */
function validateCountrySettings(countryCode, value) {
  const entry = {};

  if (value.display_mode !== undefined && value.display_mode !== null) {
    if (REGION_DISPLAY_MODES.includes(value.display_mode)) {
      entry.display_mode = value.display_mode;
    } else {
      console.warn(`[CMP] Invalid "${countryCode}.display_mode", ignoring:`, value.display_mode);
    }
  }

  if (value.button_styles !== undefined && value.button_styles !== null) {
    if (typeof value.button_styles === 'object' && !Array.isArray(value.button_styles)) {
      // 既知5キーのみ受け入れ、未知キーは捨てる
      const buttonStyles = {};
      REGION_BUTTON_KEYS.forEach((buttonKey) => {
        const style = validateButtonStyle(value.button_styles[buttonKey], `${countryCode}.button_styles.${buttonKey}`);
        if (style) buttonStyles[buttonKey] = style;
      });
      if (Object.keys(buttonStyles).length > 0) entry.button_styles = buttonStyles;
    } else {
      console.warn(`[CMP] Invalid "${countryCode}.button_styles", ignoring:`, value.button_styles);
    }
  }

  if (value.default_consent_categories !== undefined && value.default_consent_categories !== null) {
    if (
      Array.isArray(value.default_consent_categories) &&
      value.default_consent_categories.every((c) => typeof c === 'string')
    ) {
      entry.default_consent_categories = value.default_consent_categories;
    } else {
      console.warn(
        `[CMP] Invalid "${countryCode}.default_consent_categories", ignoring:`,
        value.default_consent_categories
      );
    }
  }

  return entry;
}

/**
 * ボタンスタイル（{ bg, text }）を検証する
 * 不正な色値はその色のみ捨てる。有効な色が1つもなければnullを返す
 */
function validateButtonStyle(style, path) {
  if (style === undefined || style === null) return null;
  if (typeof style !== 'object' || Array.isArray(style)) {
    console.warn(`[CMP] Invalid "${path}", ignoring:`, style);
    return null;
  }

  const validated = {};
  ['bg', 'text'].forEach((colorKey) => {
    const color = style[colorKey];
    if (color === undefined || color === null) return;
    if (typeof color === 'string' && COLOR_VALUE_REGEXP.test(color)) {
      validated[colorKey] = color;
    } else {
      console.warn(`[CMP] Invalid color value "${path}.${colorKey}", ignoring:`, color);
    }
  });

  return Object.keys(validated).length > 0 ? validated : null;
}

/**
 * 国コードからリージョン設定を解決する
 *
 * @param {Object} regionSettings - validateConfig済みのregionSettings
 * @param {string|null} country - ISO国コード（判定不能時null）
 * @returns {{ displayMode: string, buttonStyles: Object|null, defaultConsentCategories: string[]|null }}
 *   buttonStyles: { accept_all: {bg,text}, ... }（base_button_styleの場合は5ボタンすべてに同色を展開）
 */
export function resolveRegion(regionSettings, country) {
  const settings = regionSettings && typeof regionSettings === 'object' ? regionSettings : {};
  const baseStyle =
    settings[REGION_DEFAULT_KEY] && settings[REGION_DEFAULT_KEY].base_button_style
      ? settings[REGION_DEFAULT_KEY].base_button_style
      : null;
  // base_button_styleのみの場合は5ボタンすべてに同じ1組の色を適用する
  const baseButtonStyles = baseStyle
    ? REGION_BUTTON_KEYS.reduce((acc, key) => {
        acc[key] = baseStyle;
        return acc;
      }, {})
    : null;

  const countryEntry = country && country !== REGION_DEFAULT_KEY ? settings[country] : null;
  if (!countryEntry) {
    // 国別設定なし or 判定不能 → defaultを適用（通常表示・カテゴリ初期チェック=requiredのみ）
    return { displayMode: 'normal', buttonStyles: baseButtonStyles, defaultConsentCategories: null };
  }

  return {
    displayMode: countryEntry.display_mode || 'normal',
    buttonStyles: countryEntry.button_styles || baseButtonStyles,
    defaultConsentCategories: countryEntry.default_consent_categories || null
  };
}
